import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { seasonEndpoints } from '../../api-seasons';
import type { GamePublishPlan, PublishInput, SeasonGame } from '../../api-seasons';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, statusTone } from '../../ui';
import { INPUT, humanize, zonedToUtc } from '../event/shared';
import { QuickSelect, chunks, gameWhen } from './QuickSelect';
import { dateWords } from '../../terms';

/**
 * Publish games in bulk (D-371).
 *
 * Check first: for each chosen game, what it lacks (active prices, inventory,
 * published, on sale) and what the run would do about it, or why it cannot.
 * Then apply, a few games per request, through the same steps as the event's
 * own buttons.
 */

/** Games per apply request; the API allows 10. */
const APPLY_GROUP = 5;

const STEP_WORDS: Record<string, string> = {
  ACTIVATE_PRICES: 'prices activated',
  INITIALIZE_INVENTORY: 'inventory set up',
  PUBLISH: 'published',
  SCHEDULE: 'scheduled',
  PUT_ON_SALE: 'on sale',
};

export function PublishPanel({ seasonId, games, onChanged }: { seasonId: string; games: SeasonGame[]; onChanged: () => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [steps, setSteps] = useState<PublishInput['steps']>({ activate_prices: true, initialize_inventory: true, publish: true, put_on_sale: true });
  const [onSaleAt, setOnSaleAt] = useState('');
  const [plans, setPlans] = useState<Map<string, GamePublishPlan>>(new Map());
  const [lacking, setLacking] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const timezone = games.find((game) => game.timezone)?.timezone ?? 'America/Chicago';
  const body = (ids: string[], dryRun: boolean): PublishInput => ({ selection: { event_ids: ids }, steps, on_sale_at: onSaleAt ? zonedToUtc(onSaleAt, timezone)!.replace(/\.\d{3}Z$/, '.000Z') : null, dry_run: dryRun });

  const check = useMutation({
    mutationFn: () => seasonEndpoints.publish(seasonId, body([...selected], true)),
    onSuccess: (result) => {
      setPlans(new Map(result.games.map((plan) => [plan.event_id, plan])));
      setLacking(result.missing_permissions);
      setError(null);
      setProgress(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const apply = useMutation({
    mutationFn: async () => {
      const acting = [...plans.values()].filter((plan) => selected.has(plan.event_id) && plan.actions.length > 0).map((plan) => plan.event_id);
      const done = new Map(plans);
      for (const [i, group] of chunks(acting, APPLY_GROUP).entries()) {
        setProgress(`Publishing games ${i * APPLY_GROUP + 1}–${i * APPLY_GROUP + group.length} of ${acting.length}…`);
        const result = await seasonEndpoints.publish(seasonId, body(group, false));
        for (const plan of result.games) done.set(plan.event_id, plan);
        setPlans(new Map(done));
      }
      return acting.length;
    },
    onSuccess: (count) => {
      const failed = [...plans.values()].filter((plan) => plan.result && !plan.result.ok).length;
      setProgress(`${count} ${count === 1 ? 'game' : 'games'} done${failed ? `, ${failed} with a problem (see below)` : ''}.`);
      onChanged();
    },
    onError: (cause: ApiError) => {
      setProgress(null);
      setError(cause.message);
      onChanged();
    },
  });

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const checked = [...plans.values()].filter((plan) => selected.has(plan.event_id));
  const acting = checked.filter((plan) => plan.actions.length > 0).length;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel title="Publish games" description={`Choose games, check what each needs, then apply. Each game gets its draft prices activated, its inventory set up from the seating chart, the event published and the ${dateWords().one} put on sale — only the steps it still needs, and none if something is missing that this cannot fix.`}>
        <div className="flex flex-wrap items-end gap-4 border-b border-line px-5 py-3 text-sm">
          {(
            [
              ['activate_prices', 'Activate prices'],
              ['initialize_inventory', 'Set up inventory'],
              ['publish', 'Publish'],
              ['put_on_sale', 'Put on sale'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <input type="checkbox" checked={steps[key]} onChange={(e) => setSteps({ ...steps, [key]: e.target.checked })} />
              {label}
            </label>
          ))}
          <Field label="Website on sale" htmlFor="publish-at" hint={`Empty is now. In ${timezone}. The box office can sell at once.`}>
            <input id="publish-at" type="datetime-local" value={onSaleAt} onChange={(e) => setOnSaleAt(e.target.value)} className={INPUT} />
          </Field>
        </div>
        <QuickSelect games={games} onSelect={setSelected} />
        <div className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-3">
          <Button variant="primary" disabled={selected.size === 0 || check.isPending} onClick={() => check.mutate()}>
            {check.isPending ? 'Checking…' : `Check ${selected.size} ${selected.size === 1 ? 'game' : 'games'}`}
          </Button>
          <Button disabled={acting === 0 || apply.isPending || lacking.length > 0} onClick={() => window.confirm(`Publish ${acting} ${acting === 1 ? 'game' : 'games'} as checked?`) && apply.mutate()}>
            {apply.isPending ? 'Publishing…' : `Apply to ${acting} ${acting === 1 ? 'game' : 'games'}`}
          </Button>
          {lacking.length > 0 ? <span className="text-sm text-bad">You need {lacking.join(', ')} for this.</span> : null}
          {progress ? <span className="text-sm text-ink-600">{progress}</span> : null}
        </div>
        <Table headers={['', 'Game', 'Status', 'Missing', 'This run']}>
          {games.length === 0 ? (
            <EmptyRow columns={5}>No games in this season yet. Build them from the schedule first.</EmptyRow>
          ) : (
            games.map((game) => {
              const plan = plans.get(game.event_id);
              return (
                <tr key={game.event_id} className="border-b border-line align-top last:border-0">
                  <td className="px-5 py-2">
                    <input type="checkbox" aria-label={`Choose ${game.title}`} checked={selected.has(game.event_id)} onChange={() => toggle(game.event_id)} />
                  </td>
                  <td className="px-5 py-2">
                    <Link to={`/events/${game.event_id}`} className="text-ink-900 hover:underline">
                      {game.title}
                    </Link>
                    <div className="text-xs text-ink-400">
                      {gameWhen(game.starts_at, game.timezone)}
                      {game.promo_title ? ` · ${game.promo_title}` : ''}
                    </div>
                  </td>
                  <td className="px-5 py-2">
                    <Badge tone={statusTone(game.status)}>{humanize(game.status)}</Badge>
                    {game.performance_status ? <div className="mt-1 text-xs text-ink-400">{humanize(game.performance_status)}</div> : null}
                  </td>
                  <td className="px-5 py-2 text-xs text-ink-600">{(plan?.missing ?? game.missing).map((m) => m.message).join('; ') || 'Nothing'}</td>
                  <td className="px-5 py-2 text-xs">
                    {plan?.result ? (
                      plan.result.ok ? (
                        <span className="text-ok">Done: {plan.result.done.map((step) => STEP_WORDS[step]).join(', ')}</span>
                      ) : (
                        <span className="text-bad">
                          Stopped after {plan.result.done.length} {plan.result.done.length === 1 ? 'step' : 'steps'}: {plan.result.error?.message}
                        </span>
                      )
                    ) : plan ? (
                      <>
                        {plan.actions.map((action, i) => (
                          <div key={i} className="text-ink-900">
                            {action.message}
                          </div>
                        ))}
                        {plan.blockers.map((note) => (
                          <div key={note.code + note.message} className="text-bad">
                            {note.message}
                          </div>
                        ))}
                        {plan.warnings.map((note) => (
                          <div key={note.code + note.message} className="text-ink-400">
                            {note.message}
                          </div>
                        ))}
                        {plan.actions.length === 0 && plan.blockers.length === 0 ? <div className="text-ink-400">Nothing to do</div> : null}
                      </>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </Table>
      </Panel>
    </div>
  );
}
